import React from "react";
import PropTypes from "prop-types";
import moveFocus, {
    doFocusMovement,
    makeControlSelector,
    makeRecordSelector,
    placeFocus,
} from "./moveFocus";
import Rx from "rxjs";
import { debounceTime } from "rxjs/operators";
import ActiveFormActions from "data/ActiveFormActions";
import ActiveFormStore, {
    FIRST_FIELD,
    isControl,
    isElement,
    isSameControl,
} from "data/ActiveFormStore";
import ActiveSectionStore from "data/ActiveSectionStore";
import ActiveSectionActions from "data/ActiveSectionActions";
import OverStore from "data/OverStore";
import Dispatcher from "Dispatcher";
import { IMPLICIT_TOP_OF_FORM_SECTION_NAME } from "data/meta/FormStore";
import TableStore from "data/TableStore";
import NoFormView from "./NoFormView";
import NavigatorStore from "data/NavigatorStore";
import { Formform } from "lib/dataentry/lib/dataentryform/formitems/Form/form";
import BrowserStore from "data/BrowserStore";
import HiddenFieldStore from "data/HiddenFieldStore";
import useFluxStore from "@armus/use-flux-store";
import RecordBrowserActions from "data/RecordBrowserActions";
import safeContextDispatch from "data/util/safeContextDispatch";

function buildElementAction(type, currentTarget, keyboardShortcuts) {
    const $el = $(currentTarget);
    const domId = $el.attr("id");
    const ctx = $el.parents(".entity-context");
    // this is weird but the dom is holding onto the "correct" entity-key/id
    // when the event is fired and works with blur during form change.
    return {
        type,
        domId,
        entityKey: ctx.data("entity-key"),
        recordId: ctx.data("id"),
        fieldKey: $el.get(0).dataset.shortname,
        // this isn't super awesome, but it's been this way since "forever"
        keyboardShortcuts: keyboardShortcuts || $el.get(0).dataset.keyboardShortcuts,
    };
}

/*
 This should probably interrogate the descriptor to enumerate sections, and then
 only ask the DOM about positions, rather than asking the DOM to enumerate as
 well. But only so much unstupiding can happen at a time.
 */
function getSectionBounds(container) {
    const bounds = [];
    try {
        const containerOffset = container.offset().top;
        container.find(".control-section-header").each((i, e) => {
            bounds.push({
                title: $(e).data("title"),
                top: $(e).offset().top - containerOffset,
            });
        });
    } catch (error) {
        // it's unclear why this was here, but it was, and silently ignored errors?!
        console.warn("Error finding section bounds", error);
    }
    return bounds;
}

const getActiveRecord = (fluxContext, entityKey, id) => {
    const rec = NavigatorStore.getRecordState(fluxContext);
    if (rec == null) return null;
    if (rec.key !== entityKey || rec.id !== id) {
        return null;
    }
    // opting for try..catch over an existence check, because it
    // truly is exceptional that NavigationStore is stale, and
    // an existence check is relatively expensive to be done on
    // every change.
    try {
        return TableStore.getEntityProxy(rec.key, rec.id);
    } catch (e) {
        if (e.message && e.message.indexOf("record is known")) {
            return null;
        }
        throw e;
    }
};

/*
I focus the specified control, despite my name. The passed field (which
specifies the control) may be one of three things:

1.  a control data packet, likely from ActiveFormStore.getActiveControl(),
1.  an element data packet, likely from ActiveFormStore.getFocusedElement(),
1.  DEPRECATED: a string, which is shortname of the field to focus the
    fronting control of, or
1.  DEPRECATED: an array, which is unclear, but appears to be a composition
    path through a record grid control? it doesn't work, and relies on
    non-standard $control.focus method behaviour.
*/
const focusField = (container, fluxContext, field) => {
    const $container = $(container);
    if(OverStore.isModalInteractive()) {
        return; // prevent any lingering focus changes when modal is visible and interactive.
    }
    if (field.hasOwnProperty("domId") && field.domId != null) {
        const $el = $container.find("#" + field.domId);
        if ($el.length > 0) {
            if (isElement(field) || isControl(field)) {
                if ($el.parents(makeRecordSelector(field)).length > 0) {
                    return placeFocus($el);
                }
            } else {
                return placeFocus($el);
            }
        }
        console.warn("domId focus request missed", field.domId, $container);
    }
    if (isControl(field)) {
        const recordSelector = makeRecordSelector(field);
        if (field.fieldKey === FIRST_FIELD) {
            const $el = $container.is(recordSelector)
                ? $container
                : $(recordSelector);
            moveFocus(fluxContext, $el);
            return true;
        } else {
            // fall back to the selector-based stuff
            const controlSelector = makeControlSelector(field);
            const $el = $container.is(recordSelector)
                ? $(controlSelector)
                : $(recordSelector + " " + controlSelector);

            if ($el.length > 0) {
                if ($el.length > 1) {
                    console.warn(`focus request matched ${$el.length} elements`, field, $el);
                }
                // this could, in theory, match the wrong control if a child
                // record type has a field of the same name, and a grid of
                // those children are earlier in the DOM than the right one.
                return placeFocus($el);
            }
            // console.warn("didn't find", $el.selector);
        }
    } else if (typeof field === "string") {
        console.warn("DEPRECATED: EntityForm.focusField(String) w/", field);
        const $el = $(`.control-container-${field}`);
        if ($el.length > 0) {
            return placeFocus($el);
        }
    } else {
        console.error("EntityForm.focusField was passed something unexpected", field);
    }
    return false;
};

const doResize = (container) => {
    // this should be passed down as a prop into Form/form.js
    // leaving it as is for now.
    const $el = $(container);
    $el.find("form > div > :last-child").css({"min-height": $el.find(".entity-information").height()});
};

const uniqueEntityFormId = (fluxContext, entityKey, id, descriptor) =>
    `${fluxContext}.${entityKey}.${id}.${descriptor.id}`;

const EntityForm = (props) => {
    const {fluxContext, entityKey, id, descriptor} = props;
    const firstRenderRef = React.useRef(null);
    const lastSectionRef = React.useRef(ActiveSectionStore.getCurrentSection(fluxContext));
    const entityFormRef = React.useRef();
    const scrollSubscription = React.useRef(null);
    const scrollContainerRef = React.useRef();

    const state = useFluxStore(() => ({
        activeRecord: getActiveRecord(fluxContext, entityKey, id),
        size: BrowserStore.getSize(),
        section: ActiveSectionStore.getCurrentSection(fluxContext)
    })
    , [
        BrowserStore,
        ActiveFormStore,
        TableStore,
        NavigatorStore,
        HiddenFieldStore,
        ActiveSectionStore,
    ], [fluxContext, entityKey, id]);
    const {activeRecord, section} = state;

    const uniqueId = uniqueEntityFormId(fluxContext, entityKey, id, descriptor);
    if(firstRenderRef.current !== uniqueId) {
        firstRenderRef.current = uniqueId;
        // This START_RENDER and RENDERED should eventually be consolidated.
        Dispatcher.contextDispatch(fluxContext, {
            type: ActiveFormActions.START_RENDER,
            key: entityKey,
            id: id,
        });
    }

    React.useEffect(() => {
        const $scrollContainer = $(scrollContainerRef.current);
        if(scrollSubscription.current !== null) {
            scrollSubscription.current.unsubscribe();
        }
        scrollSubscription.current = Rx.Observable.fromEvent(
            $scrollContainer[0],
            "scroll",
        )
            .pipe(
                debounceTime(100),
            )
            .subscribe(() => {
                let section;
                const bounds = getSectionBounds($scrollContainer);
                for (const item of bounds) {
                    if (item.top <= 0) {
                        section = item;
                    } else {
                        break;
                    }
                }
                lastSectionRef.current = section ? section.title : IMPLICIT_TOP_OF_FORM_SECTION_NAME;
                Dispatcher.contextDispatch(fluxContext, {
                    type: ActiveSectionActions.SCROLL,
                    key: entityKey,
                    id: id,
                    section: lastSectionRef.current,
                });
            });

        Dispatcher.contextDispatch(fluxContext, {
            type: ActiveFormActions.RENDERED,
            key: entityKey,
            id: id,
        });
        return () => {
            // on destroy
            scrollSubscription.current.unsubscribe();
        };
    }, [fluxContext, entityKey, id, descriptor]);

    React.useEffect(() => {
        if(section !== lastSectionRef.current) {
            const $scrollContainer = $(scrollContainerRef.current);
            if (section === IMPLICIT_TOP_OF_FORM_SECTION_NAME) {
                $scrollContainer.scrollTop(0);
            } else {
                const target = $(`.control-section-header[data-title='${section}']`);
                const offset = target.offset().top - $scrollContainer.offset().top;
                $scrollContainer.scrollTop(offset + $scrollContainer.scrollTop());
            }
        }
    }, [section, lastSectionRef.current]);

    React.useEffect(() => {
        // called after every render
        doResize(entityFormRef.current);

        const fm = ActiveFormStore.getFocusMovement(fluxContext);
        if (fm != null) {
            doFocusMovement($(entityFormRef.current), fm);
            return;
        }
        const ac = ActiveFormStore.getActiveControl(fluxContext);
        const lb = ActiveFormStore.getLastBlurredControl(fluxContext);
        if (ac != null && !isSameControl(ac, lb)) {
            focusField(entityFormRef.current, fluxContext, ac);
        }
    });

    const onFocus = React.useCallback((e, k) => {
        e.stopPropagation();
        e.preventDefault();
        safeContextDispatch(
            fluxContext,
            buildElementAction(
                ActiveFormActions.FOCUS_ELEMENT,
                e.currentTarget,
                k,
            ),
        );
    }, [fluxContext, entityKey, id]);

    const onBlur = React.useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        safeContextDispatch(
            fluxContext,
            buildElementAction(
                ActiveFormActions.BLUR_ELEMENT,
                e.currentTarget
            ),
        );
    }, [fluxContext, entityKey, id]);

    const onKeyDown = React.useCallback((e, isKeyboardShortcut) => {
        const $el = $(entityFormRef.current);
        if (isKeyboardShortcut) {
            e.preventDefault();
            e.stopPropagation();
            let tgt = e.target;
            tgt.blur();
            if (!tgt.classList.contains("react-control")) {
                // oof.
                tgt = $(tgt).parents(".react-control")[0];
            }
            // This solves move focus happening too soon and gives
            // onCommitChange a chance to change field enablement.
            // wait a tick before trying to move.
            //
            // eslint-disable-next-line no-settimeout
            setTimeout(() => {
                moveFocus(fluxContext, $(entityFormRef.current), tgt);
            });

            return;
        }
        if (!([9, 13].includes(e.keyCode)) || e.altKey || e.ctrlKey) {
            // we're only interested in TAB and ENTER, optionally with SHIFT
            // pressed. Otherwise we don't care.
            return;
        }
        const forward = !e.shiftKey;
        e.target.blur();
        const $tgt = $(e.target);
        const $ctrl = $tgt.parents(".control");
        if ($ctrl.length > 0) {
            moveFocus(fluxContext, $el, $ctrl, forward);
        } else {
            moveFocus(fluxContext, $el, $tgt, forward);
        }
    }, [fluxContext, entityKey, id]);

    const onGlobalRecordLookup = React.useCallback((key, field, candidateFilter, currId) => {
        const lookupCallback = ((ar, field, key) =>
            (rbs, model) => ar.set(field, model.getProjectionForVersion(key).getRecord()))(activeRecord, field, key);

        const focusCallback = () => focusField(entityFormRef.current, fluxContext, field);

        Dispatcher.dispatch({
                type: RecordBrowserActions.GLOBAL_LOOKUP_CLICK,
                key,
                lookupCallback,
                focusCallback,
                candidateFilter,
                currId,
            });
    }, [fluxContext, entityKey, id]);

    return (
        <div
            ref={entityFormRef}
            id={"data-entity-form"}
            className="entity-context"
            data-context={fluxContext}
            data-id={activeRecord != null ? activeRecord.id : ""}
            data-entity-key={activeRecord != null ? activeRecord.entityKey : ""}
        >
            <div ref={scrollContainerRef} className="entity-information">
                <div className="form-target">
                    {activeRecord == null ? (
                        <NoFormView />
                    ) : (
                        <Formform
                            selector="i-am-the-form-body-yo"
                            descriptor={descriptor}
                            activeRecord={activeRecord}
                            fluxContext={fluxContext}
                            onFocus={onFocus}
                            onBlur={onBlur}
                            onKeyDown={onKeyDown}
                            onGlobalRecordLookup={onGlobalRecordLookup}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
EntityForm.propTypes = {
    fluxContext: PropTypes.string.isRequired,
    entityKey: PropTypes.string.isRequired,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    descriptor: PropTypes.object.isRequired
};

export default EntityForm;



// WEBPACK FOOTER //
// ./app/lib/dataentry/lib/dataentryform/views/EntityForm.js